import { useMemo, useState, useEffect } from "react";
import { PiggyBank, TrendingDown, Info, FileDown, HeartPulse, Car, LineChart, Package } from "lucide-react";
import { useCountUp } from "@/lib/useCountUp";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { generateUnifiedHandoff } from "@/lib/unifiedHandoff";

// ============================================================================
// Solo 401(k) Retirement Runway Matrix — Premium Tax Shield container.
// Interactive slider maps localized pre-tax contribution boundaries against
// the IRS 2026 sole-proprietor limits ($24,000 employee elective deferral +
// employer profit-sharing up to 25% of net SE income, combined ceiling
// $71,000 pre-catchup).  As the user drags the slider we recompute their
// active 1099 tax liability in real time and roll a live tax-savings odometer.
//
// Numbers used here are the best-available published caps as of the 2026
// planning cycle.  Update IRS_LIMITS_2026 when Rev. Proc. 2026-XX drops.
// ============================================================================
const IRS_LIMITS_2026 = {
  elective_deferral: 24000,       // §402(g) — pre-catchup
  combined_ceiling:  71000,       // §415(c) — pre-catchup
  employer_share_pct: 0.25,       // Net SE income share for profit-sharing
  se_tax_rate:       0.9235 * 0.153, // Effective SE tax on net earnings (~14.13%)
  hsa_self:   4400,               // §223(b)(2)(A) self-only HDHP cap (2026 projection)
  hsa_family: 8750,               // §223(b)(2)(B) family HDHP cap (2026 projection)
};

// Progressive federal ordinary-income brackets (single filer, 2026 projection)
const FED_BRACKETS_2026 = [
  { upTo: 11_925,  rate: 0.10 },
  { upTo: 48_475,  rate: 0.12 },
  { upTo: 103_350, rate: 0.22 },
  { upTo: 197_300, rate: 0.24 },
  { upTo: 250_525, rate: 0.32 },
  { upTo: 626_350, rate: 0.35 },
  { upTo: Infinity,rate: 0.37 },
];

function federalTaxOn(taxable) {
  if (taxable <= 0) return 0;
  let owed = 0;
  let prev = 0;
  for (const b of FED_BRACKETS_2026) {
    const slice = Math.min(taxable, b.upTo) - prev;
    if (slice > 0) owed += slice * b.rate;
    if (taxable <= b.upTo) return owed;
    prev = b.upTo;
  }
  return owed;
}

function computeRunway(net1099, contributionPct, opts = {}) {
  const {
    hsaPct = 0,
    hsaCap = IRS_LIMITS_2026.hsa_self,
    mileageDeduction = 0,
  } = opts;
  const pct = Math.max(0, Math.min(1, contributionPct / 100));
  const adjNet = Math.max(0, net1099 - Math.max(0, mileageDeduction));
  const rawContribution = adjNet * pct;
  const employerCap = adjNet * IRS_LIMITS_2026.employer_share_pct + IRS_LIMITS_2026.elective_deferral;
  const legalCap = Math.min(IRS_LIMITS_2026.combined_ceiling, employerCap);
  const contribution = Math.min(rawContribution, legalCap);
  const hsaPctBounded = Math.max(0, Math.min(1, (hsaPct || 0) / 100));
  const hsaContribution = Math.min(hsaCap * hsaPctBounded, hsaCap);
  const taxableBefore = Math.max(0, net1099);
  const taxableAfter = Math.max(0, adjNet - contribution - hsaContribution);
  const fedBefore = federalTaxOn(taxableBefore);
  const fedAfter = federalTaxOn(taxableAfter);
  const seBefore = taxableBefore * IRS_LIMITS_2026.se_tax_rate;
  const seAfter = taxableAfter * IRS_LIMITS_2026.se_tax_rate;
  const totalBefore = fedBefore + seBefore;
  const totalAfter = fedAfter + seAfter;
  return {
    contribution,
    hsaContribution,
    hsaCap,
    mileageDeduction: Math.max(0, mileageDeduction),
    legalCap,
    cappedByStatute: rawContribution > legalCap,
    taxableAfter,
    totalBefore,
    totalAfter,
    savings: Math.max(0, totalBefore - totalAfter),
    zeroOut: totalAfter <= 0.5,
  };
}

const fmt = (n) => "$" + Number(Math.round(n)).toLocaleString();

export function RetirementRunwayConsole({ defaultNet1099 = 92000 }) {
  const [net, setNet] = useState(defaultNet1099);
  const [pct, setPct] = useState(15);
  const [hsaPct, setHsaPct] = useState(0);
  const [hsaCoverage, setHsaCoverage] = useState("self"); // 'self' | 'family'
  const [mileageDeduction, setMileageDeduction] = useState(0);
  const [showGrowth, setShowGrowth] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const hsaCap = hsaCoverage === "family" ? IRS_LIMITS_2026.hsa_family : IRS_LIMITS_2026.hsa_self;
  const runway = useMemo(
    () => computeRunway(net, pct, { hsaPct, hsaCap, mileageDeduction }),
    [net, pct, hsaPct, hsaCap, mileageDeduction],
  );
  const liabilityRolled = useCountUp(runway.totalAfter, 900);
  const savingsRolled = useCountUp(runway.savings, 900);
  const contribRolled = useCountUp(runway.contribution, 900);
  const hsaRolled = useCountUp(runway.hsaContribution, 900);

  // ---------- Mileage pipeline hook — pulls the summed YTD business-mileage
  // deduction from /api/mileage and folds it into the runway math on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/mileage");
        if (cancelled) return;
        const ded = Number(data?.business_mileage_deduction || 0);
        if (ded > 0) setMileageDeduction(ded);
      } catch {
        // Silent — pipeline is opportunistic; console still works standalone.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------- Print-ready PDF binder — one-page CPA-deliverable ----------
  // Uses jsPDF with core fonts so we ship no font subsets and the file stays
  // under 20 KB. Layout is deliberately monochrome-friendly for laser printers.
  const exportPDF = () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const M = 48;
      const now = new Date();
      const stamp = now.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

      // Header rule
      doc.setDrawColor(20, 20, 20);
      doc.setLineWidth(1.2);
      doc.line(M, 60, W - M, 60);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Solo 401(k) · Retirement Runway Simulation", M, 84);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(`Prepared ${stamp} · Receipt Sheets · CPA Deliverable`, M, 100);
      doc.setTextColor(0);

      // Inputs block
      let y = 140;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Simulation Inputs", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(11);
      doc.text(`Annualized net 1099 income:`, M, y);
      doc.text(fmt(net), W - M, y, { align: "right" }); y += 18;
      doc.text(`Retirement sweep percentage:`, M, y);
      doc.text(`${pct}% of net profit`, W - M, y, { align: "right" }); y += 18;
      doc.text(`Statutory ceiling (§415(c)):`, M, y);
      doc.text(fmt(runway.legalCap), W - M, y, { align: "right" });

      // Results block
      y += 36;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Contribution & Tax Impact", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(11);
      const rows = [
        ["Solo 401(k) contribution",           fmt(runway.contribution)],
        ["Taxable income (after sweep)",       fmt(runway.taxableAfter)],
        ["Federal + SE tax · before sweep",    fmt(runway.totalBefore)],
        ["Federal + SE tax · after sweep",     fmt(runway.totalAfter)],
        ["Total tax cut",                      fmt(runway.savings)],
      ];
      rows.forEach(([k, v]) => {
        doc.text(k, M, y);
        doc.text(v, W - M, y, { align: "right" });
        y += 18;
      });

      // Zero-liability callout
      if (runway.zeroOut) {
        y += 14;
        doc.setFillColor(212, 245, 224);
        doc.roundedRect(M, y - 12, W - M * 2, 40, 6, 6, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(12);
        doc.setTextColor(4, 88, 34);
        doc.text("Projected liability zero'd — sweep fully offsets 2026 tax burden.", M + 14, y + 12);
        doc.setTextColor(0);
      }

      // Statutory basis block
      y = Math.max(y + 48, 500);
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Statutory Basis (IRS 2026)", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const statLines = [
        "IRC § 402(g) — Employee elective deferral cap: $24,000 (2026 projection).",
        "IRC § 415(c) — Combined annual additions ceiling: $71,000 (2026 projection).",
        "IRC § 401(c)(2) — Net earnings from self-employment define contribution base.",
        "IRC § 1401(a) — Self-employment tax coupling (12.4% OASDI + 2.9% Medicare on 92.35%).",
        "Progressive federal brackets applied per Rev. Proc. 2025-XX projections (10%–37%).",
      ];
      statLines.forEach((line) => { doc.text(line, M, y); y += 14; });

      // Footer disclaimer
      y = 720;
      doc.setDrawColor(160); doc.line(M, y, W - M, y);
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(110);
      doc.text(
        "This document is a projection based on user-supplied inputs and is not legal or tax advice.",
        M, y + 14,
      );
      doc.text(
        "Confirm final Solo 401(k) contributions with a qualified CPA and current-year Rev. Proc. tables.",
        M, y + 26,
      );

      const fname = `retirement_runway_${now.toISOString().slice(0,10)}.pdf`;
      doc.save(fname);
      toast.success("Retirement Runway PDF exported", { description: fname });
    } catch (err) {
      toast.error("PDF export failed — try again");
    }
  };

  return (
    <div
      data-testid="retirement-runway-console"
      className="glass rounded-2xl p-6 relative overflow-hidden"
      style={{ borderColor: "rgba(168,85,247,0.35)" }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(168,85,247,0.28), transparent 65%)",
          filter: "blur(20px)",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-purple-300 flex items-center gap-1.5">
            <PiggyBank size={12} /> Solo 401(k) · Retirement Runway
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportPDF}
              data-testid="retirement-export-pdf-btn"
              className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-100 hover:bg-purple-500/20 hover:-translate-y-px transition-all flex items-center gap-1.5"
              title="Export a print-ready one-page PDF for your CPA"
            >
              <FileDown size={11} /> Export PDF
            </button>
            <div
              data-testid="retirement-zero-liability"
              data-zero={runway.zeroOut ? "true" : "false"}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border ${runway.zeroOut ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200" : "border-slate-500/40 text-slate-400"}`}
            >
              {runway.zeroOut ? "🟢 Liability zero'd" : "Simulating…"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          {/* Live liability odometer */}
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
              Active 1099 tax liability
            </div>
            <div
              data-testid="retirement-liability-odometer"
              className="text-3xl font-mono font-bold tracking-tight mt-1"
              style={{
                background: "linear-gradient(90deg, #f87171, #fde047)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 6px rgba(248,113,113,0.35))",
              }}
            >
              {fmt(liabilityRolled)}
            </div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
              Was {fmt(runway.totalBefore)} · Fed + SE combined
            </div>
          </div>
          {/* Savings odometer */}
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <TrendingDown size={10} /> Tax cut from sweep
            </div>
            <div
              data-testid="retirement-savings-odometer"
              className="text-3xl font-mono font-bold tracking-tight mt-1"
              style={{
                background: "linear-gradient(90deg, #10b981, #67e8f9)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 0 6px rgba(16,185,129,0.4))",
              }}
            >
              {fmt(savingsRolled)}
            </div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
              wealth stays yours · tax-deferred
            </div>
          </div>
          {/* Contribution */}
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
              Solo 401(k) contribution
            </div>
            <div
              data-testid="retirement-contribution-odometer"
              className="text-3xl font-mono font-bold tracking-tight mt-1 text-purple-200"
              style={{ filter: "drop-shadow(0 0 6px rgba(168,85,247,0.4))" }}
            >
              {fmt(contribRolled)}
            </div>
            <div className="text-[10px] font-mono mt-0.5" style={{ color: runway.cappedByStatute ? "#fca5a5" : "#94a3b8" }}>
              {runway.cappedByStatute ? `Capped at ${fmt(runway.legalCap)} · IRS §415(c)` : `Under §415(c) cap of ${fmt(runway.legalCap)}`}
            </div>
          </div>
        </div>

        {/* Net 1099 input */}
        <div className="mb-4">
          <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-purple-300 flex items-center gap-1.5 mb-2">
            Annualized net 1099 income
            <span title="Enter your projected self-employment net earnings before retirement contribution." className="text-slate-500"><Info size={11} /></span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              step="1000"
              value={net}
              onChange={(e) => setNet(Math.max(0, Number(e.target.value || 0)))}
              data-testid="retirement-net-input"
              className="w-40 rounded-lg bg-slate-900/70 border border-purple-500/25 focus:border-purple-400 outline-none text-white text-sm p-2 font-mono"
            />
            <span className="text-slate-500 text-xs font-mono">USD · pre-contribution</span>
          </div>
        </div>

        {/* Contribution slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-purple-300">
              Retirement sweep · {pct}% of profit
            </label>
            <span className="text-[10px] font-mono text-slate-400">
              max legal = {Math.round((runway.legalCap / Math.max(1, net)) * 100)}%
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={pct}
            onChange={(e) => setPct(Number(e.target.value))}
            data-testid="retirement-sweep-slider"
            className="w-full accent-purple-500"
            style={{ height: 8 }}
          />
          <div className="mt-2 flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-slate-500">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* HSA · Triple Tax Shield */}
        <div className="mt-6 rounded-xl p-4 border border-emerald-500/30 bg-emerald-500/5" data-testid="retirement-hsa-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-300 flex items-center gap-1.5">
              <HeartPulse size={12} /> HSA · Triple Tax Shield · IRC § 223
            </div>
            <div className="inline-flex rounded-md border border-emerald-500/40 overflow-hidden" data-testid="retirement-hsa-coverage-toggle">
              {[
                { key: "self",   label: `Self · ${fmt(IRS_LIMITS_2026.hsa_self)}` },
                { key: "family", label: `Family · ${fmt(IRS_LIMITS_2026.hsa_family)}` },
              ].map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setHsaCoverage(o.key)}
                  data-testid={`retirement-hsa-coverage-${o.key}`}
                  data-active={hsaCoverage === o.key ? "true" : "false"}
                  className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors ${hsaCoverage === o.key ? "bg-emerald-500/25 text-emerald-100" : "text-emerald-300/70 hover:text-white hover:bg-emerald-500/10"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">HSA contribution</div>
              <div
                data-testid="retirement-hsa-odometer"
                className="text-2xl font-mono font-bold tracking-tight mt-1 text-emerald-200"
                style={{ filter: "drop-shadow(0 0 6px rgba(16,185,129,0.35))" }}
              >
                {fmt(hsaRolled)}
              </div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                Cap {fmt(hsaCap)} · fully above-the-line
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-300">
                  HSA sweep · {hsaPct}%
                </label>
                <span className="text-[10px] font-mono text-slate-400">of {hsaCoverage} cap</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={hsaPct}
                onChange={(e) => setHsaPct(Number(e.target.value))}
                data-testid="retirement-hsa-slider"
                className="w-full accent-emerald-500"
                style={{ height: 8 }}
              />
              <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-slate-500">
                Tax-free deposit · tax-free growth · tax-free qualified medical withdrawal
              </div>
            </div>
          </div>
        </div>

        {/* Mileage · pipeline banner */}
        {mileageDeduction > 0 && (
          <div
            data-testid="retirement-mileage-pipeline"
            className="mt-4 flex items-center gap-3 rounded-lg p-3 border border-cyan-500/30 bg-cyan-500/5"
          >
            <Car size={14} className="text-cyan-300" />
            <div className="text-[11px] font-mono uppercase tracking-widest text-cyan-200">
              Mileage Vault pipeline · {fmt(mileageDeduction)} business mileage folded into your 401(k) contribution base
            </div>
          </div>
        )}

        {/* HSA · 30-Year Wealth Projection Toggle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowGrowth((v) => !v)}
            data-testid="hsa-growth-toggle"
            data-active={showGrowth ? "true" : "false"}
            className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-md border flex items-center gap-1.5 transition-colors ${showGrowth ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-100" : "border-emerald-500/30 text-emerald-300 hover:text-white hover:bg-emerald-500/10"}`}
          >
            <LineChart size={11} /> {showGrowth ? "Hide" : "Show"} 30-Year Wealth Projection
          </button>
          {showGrowth && (() => {
            const annual = Math.max(0, runway.hsaContribution);
            const rate = 0.07;
            const years = 30;
            const points = [];
            let bal = 0;
            for (let y = 1; y <= years; y++) {
              bal = (bal + annual) * (1 + rate);
              points.push({ y, bal });
            }
            const maxBal = points[points.length - 1].bal || 1;
            const svgW = 640, svgH = 180, pad = 30;
            const px = (i) => pad + (i / (years - 1)) * (svgW - pad * 2);
            const py = (v) => svgH - pad - (v / maxBal) * (svgH - pad * 2);
            const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${px(i).toFixed(1)},${py(p.bal).toFixed(1)}`).join(" ");
            const area = `${path} L${px(years - 1).toFixed(1)},${svgH - pad} L${pad},${svgH - pad} Z`;
            return (
              <div
                data-testid="hsa-growth-projector"
                data-final-balance={Math.round(maxBal)}
                className="mt-3 rounded-xl p-4 border border-emerald-500/30 bg-slate-950/50"
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                    30-Year HSA Wealth Projection · 7% CAGR
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-200">
                    Final balance · <span className="text-white font-bold" data-testid="hsa-growth-final">{fmt(maxBal)}</span>
                  </div>
                </div>
                <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto">
                  <defs>
                    <linearGradient id="hsa-growth-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.55" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0.25, 0.5, 0.75].map((f) => (
                    <line key={f} x1={pad} x2={svgW - pad} y1={svgH - pad - f * (svgH - pad * 2)} y2={svgH - pad - f * (svgH - pad * 2)} stroke="#334155" strokeDasharray="2 4" />
                  ))}
                  <path d={area} fill="url(#hsa-growth-fill)" />
                  <path d={path} fill="none" stroke="#34d399" strokeWidth="2" />
                  {[9, 19, 29].map((i) => (
                    <g key={i}>
                      <circle cx={px(i)} cy={py(points[i].bal)} r="3" fill="#fde047" />
                      <text x={px(i)} y={py(points[i].bal) - 8} textAnchor="middle" fill="#fde047" fontSize="9" fontFamily="ui-monospace, monospace">
                        Yr {i + 1} · {fmt(points[i].bal)}
                      </text>
                    </g>
                  ))}
                </svg>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  Contributions grow tax-free under IRC § 223 · Qualified medical withdrawals also tax-free
                </div>
              </div>
            );
          })()}
        </div>

        {/* Unified CPA Handoff PDF Bundle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={async () => {
              if (handoffBusy) return;
              setHandoffBusy(true);
              const toastId = toast.loading("Compiling Unified CPA Handoff Packet…");
              try {
                const result = await generateUnifiedHandoff({
                  runway: {
                    net,
                    pct,
                    contribution: runway.contribution,
                    hsaContribution: runway.hsaContribution,
                    mileageDeduction: runway.mileageDeduction,
                    totalBefore: runway.totalBefore,
                    totalAfter: runway.totalAfter,
                    savings: runway.savings,
                  },
                  roth: { filing: "single", income: net, determination: "See Backdoor Roth Matrix widget" },
                  onProgress: (m) => toast.loading(m, { id: toastId }),
                });
                toast.success(`Packet compiled · ${result.receipt_count} audit-safe receipts bundled`, {
                  id: toastId,
                  description: result.fname,
                });
              } catch (e) {
                toast.error("Unified handoff export failed", { id: toastId });
              } finally {
                setHandoffBusy(false);
              }
            }}
            disabled={handoffBusy}
            data-testid="unified-handoff-btn"
            className={`w-full h-11 px-4 rounded-lg text-slate-950 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-transform ${handoffBusy ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
            style={{
              background: "linear-gradient(135deg, #fde047, #10b981, #67e8f9)",
              boxShadow: "0 10px 24px -12px rgba(16,185,129,0.6)",
            }}
          >
            <Package size={14} /> {handoffBusy ? "Compiling…" : "⚡ Generate Unified CPA Handoff PDF Bundle"}
          </button>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center">
            Stitches Retirement Runway + Backdoor Roth + Audit-Safe ledger into one bank-grade PDF
          </div>
        </div>

        <div className="mt-5 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-t border-white/5 pt-3">
          IRS 2026 · §402(g) · §415(c) · §223 HSA · §1401(a) SE-tax coupling
        </div>
      </div>
    </div>
  );
}

export default RetirementRunwayConsole;
